import { useEffect, useState } from "react";
import { adminShopifyStats } from "../lib/api";
import { useAdmin } from "../lib/admin";
import { TrendingUp, Package, DollarSign, ShoppingBag, RefreshCw } from "lucide-react";

const fmt = (amount, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const AdminStatsWidget = () => {
  const { password } = useAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminShopifyStats(password);
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div
        data-testid="admin-stats-loading"
        className="bg-[#fdfaf3] border border-border/60 rounded-sm p-6 text-center text-sm text-muted-foreground"
      >
        Loading your store stats…
      </div>
    );
  }

  if (error) {
    const needsAuth = error.includes("read_orders") || error.includes("re-authorize");
    return (
      <div
        data-testid="admin-stats-error"
        className="bg-[#fdf6f0] border border-[#B8935F]/40 rounded-sm p-6"
      >
        <div className="text-sm font-semibold text-[hsl(var(--primary))] mb-2">
          Store stats need reconnection
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {needsAuth
            ? "To see sales & orders, I need permission to read your Shopify orders. Click below to grant it (takes 5 seconds)."
            : error}
        </p>
        {needsAuth && (
          <a
            href="/api/shopify/install"
            className="inline-flex items-center gap-2 text-xs bg-[hsl(var(--primary))] text-white px-3 py-2 rounded-sm hover:bg-[hsl(var(--primary))]/90"
          >
            Reconnect Shopify
          </a>
        )}
        <button
          onClick={load}
          className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  if (!stats) return null;

  const cur = stats.currency || "USD";

  return (
    <div data-testid="admin-stats-widget" className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#fdfaf3] border border-border/60 rounded-sm p-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            <DollarSign className="w-3 h-3" /> Revenue · 7d
          </div>
          <div className="font-serif text-2xl text-[hsl(var(--primary))]">
            {fmt(stats.revenue_7d, cur)}
          </div>
        </div>
        <div className="bg-[#fdfaf3] border border-border/60 rounded-sm p-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            <ShoppingBag className="w-3 h-3" /> Orders · 7d
          </div>
          <div className="font-serif text-2xl text-foreground">{stats.orders_7d}</div>
        </div>
        <div className="bg-[#fdfaf3] border border-border/60 rounded-sm p-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            <TrendingUp className="w-3 h-3" /> Orders · 30d
          </div>
          <div className="font-serif text-2xl text-foreground">{stats.orders_30d}</div>
        </div>
      </div>

      {/* Top products */}
      {stats.top_products?.length > 0 && (
        <div className="bg-white border border-border/60 rounded-sm p-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            <Package className="w-3 h-3" /> Top sellers · Last 7 days
          </div>
          <ul className="space-y-1.5">
            {stats.top_products.map((p) => (
              <li key={p.title} className="flex justify-between text-sm gap-3">
                <span className="truncate text-foreground/90">{p.title}</span>
                <span className="text-muted-foreground shrink-0">
                  {p.units} sold · {fmt(p.revenue, cur)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent orders */}
      {stats.recent_orders?.length > 0 && (
        <div className="bg-white border border-border/60 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Latest orders
          </div>
          <ul className="space-y-1.5">
            {stats.recent_orders.map((o) => (
              <li key={o.id} className="flex justify-between text-sm gap-3">
                <span className="truncate">
                  <span className="text-foreground/90">{o.name}</span>{" "}
                  <span className="text-muted-foreground">· {o.customer}</span>
                </span>
                <span className="text-muted-foreground shrink-0">
                  {fmt(o.total, o.currency || cur)} · {timeAgo(o.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        onClick={load}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <RefreshCw className="w-3 h-3" /> Refresh
      </button>
    </div>
  );
};

export default AdminStatsWidget;
